/**
 * S3 Service for photo upload and deletion
 * Now uses backend API for all operations instead of direct AWS SDK calls
 */

import * as api from './api';
import { UploadResult, UPLOAD_CONSTRAINTS, isValidImageType, isValidFileSize } from '../types/photo';

// Gate debug logging behind NODE_ENV for development use only
const debugLog = (...args: any[]) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(...args);
  }
};

debugLog('[DEBUG s3Service] Module loaded');

// API response wrapper type (backend wraps all responses)
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

/**
 * Upload a photo to S3 via backend API
 * @param file - The file to upload
 * @returns Upload result with success status and S3 key
 *
 * Process:
 * 1. Request presigned URL from backend: POST /photos/upload
 * 2. Upload file directly to S3 using presigned URL
 * 3. Backend handles photo metadata creation
 */
export const uploadPhotoToS3 = async (file: File): Promise<UploadResult> => {
  debugLog('[DEBUG uploadPhotoToS3] Starting upload for file:', file.name);

  // Validate file type
  if (!isValidImageType(file)) {
    console.error('[DEBUG uploadPhotoToS3] Invalid file type:', file.type);
    return {
      success: false,
      error: `Invalid file type: ${file.type}. Allowed types: ${UPLOAD_CONSTRAINTS.ALLOWED_TYPES.join(', ')}`
    };
  }

  // Validate file size
  if (!isValidFileSize(file)) {
    console.error('[DEBUG uploadPhotoToS3] File too large:', file.size);
    return {
      success: false,
      error: `File too large. Maximum size: ${UPLOAD_CONSTRAINTS.MAX_FILE_SIZE / (1024 * 1024)}MB`
    };
  }

  try {
    // Step 1: Get presigned URL from backend
    debugLog('[DEBUG uploadPhotoToS3] Requesting presigned URL');
    const response = await api.post<ApiResponse<{ uploadUrl: string; s3Key: string; photoId: string }>>(
      '/photos/upload',
      {
        filename: file.name,
        contentType: file.type,
        fileSize: file.size
      }
    );

    const { uploadUrl, s3Key, photoId } = response.data;
    debugLog('[DEBUG uploadPhotoToS3] Received presigned URL for key:', s3Key, 'photoId:', photoId);

    // Step 2: Upload file directly to S3 using presigned URL
    debugLog('[DEBUG uploadPhotoToS3] Uploading to S3');
    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type
      },
      body: file
    });

    if (!uploadResponse.ok) {
      console.error('[DEBUG uploadPhotoToS3] S3 upload failed:', uploadResponse.status);
      return {
        success: false,
        error: `Failed to upload photo to S3: ${uploadResponse.statusText}`
      };
    }

    // Step 3: Create photo metadata in DynamoDB
    debugLog('[DEBUG uploadPhotoToS3] Creating photo metadata');
    const metadataResponse = await api.post<ApiResponse<any>>('/photos', {
      photoId,
      s3Key,
      filename: file.name,
      contentType: file.type,
      fileSize: file.size
    });

    if (!metadataResponse.success) {
      console.error('[DEBUG uploadPhotoToS3] Failed to create metadata');
      // Photo uploaded but metadata failed - still return success with warning
      console.warn('[DEBUG uploadPhotoToS3] Photo uploaded but metadata creation failed');
    }

    debugLog('[DEBUG uploadPhotoToS3] Upload successful:', s3Key);
    return {
      success: true,
      s3Key,
      photoId
    };
  } catch (error) {
    console.error('[DEBUG uploadPhotoToS3] Error uploading photo:', error);
    return {
      success: false,
      error: `Failed to upload photo: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
};

/**
 * Delete a photo from S3 via backend API (requires authentication)
 * API endpoint: DELETE /photos/{id}
 *
 * Backend handles deletion of both original and thumbnail
 */
export const deletePhotoFromS3 = async (photoId: string): Promise<{ success: boolean; error?: string }> => {
  debugLog('[DEBUG deletePhotoFromS3] Deleting photo:', photoId);

  try {
    await api.del(`/photos/${photoId}`);
    debugLog('[DEBUG deletePhotoFromS3] Delete successful:', photoId);
    return { success: true };
  } catch (error) {
    console.error('[DEBUG deletePhotoFromS3] Error deleting photo:', error);
    return {
      success: false,
      error: `Failed to delete photo: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
};

/**
 * Delete multiple photos from S3 via backend API
 * @param photoIds - Array of photo IDs to delete
 */
export const deletePhotosFromS3 = async (
  photoIds: string[]
): Promise<{ success: boolean; errors: string[] }> => {
  debugLog('[DEBUG deletePhotosFromS3] Deleting', photoIds.length, 'photos');

  const errors: string[] = [];

  for (const photoId of photoIds) {
    const result = await deletePhotoFromS3(photoId);
    if (!result.success && result.error) {
      errors.push(`${photoId}: ${result.error}`);
    }
  }

  return {
    success: errors.length === 0,
    errors
  };
};

/**
 * List all photos via backend API
 * API endpoint: GET /photos
 */
export const listPhotosInS3 = async (): Promise<string[]> => {
  debugLog('[DEBUG listPhotosInS3] Listing photos');

  try {
    const response = await api.get<ApiResponse<{ s3Keys: string[] }>>('/photos');
    const s3Keys = response.data?.s3Keys || [];
    debugLog('[DEBUG listPhotosInS3] Found', s3Keys.length, 'photos');
    return s3Keys;
  } catch (error) {
    console.error('[DEBUG listPhotosInS3] Error listing photos:', error);
    return [];
  }
};

/**
 * Get the public URL for a photo
 * @param s3Key - The S3 object key
 */
export const getPhotoUrl = (s3Key: string): string => {
  const bucketName = process.env.REACT_APP_S3_BUCKET_NAME || 'www.spitfirebraai.co.za';
  return `https://${bucketName}/${s3Key}`;
};

/**
 * Get the thumbnail URL for a photo
 * @param s3Key - The S3 object key of the original photo
 */
export const getThumbnailUrl = (s3Key: string): string => {
  const bucketName = process.env.REACT_APP_S3_BUCKET_NAME || 'www.spitfirebraai.co.za';
  const thumbnailKey = s3Key.replace('photos/', 'photos/thumbnails/').replace(/\.[^/.]+$/, '.jpg');
  return `https://${bucketName}/${thumbnailKey}`;
};

/**
 * Check if API is available for photo operations
 */
export const isPhotoServiceAvailable = (): boolean => {
  const apiUrl = process.env.REACT_APP_API_URL;
  debugLog('[DEBUG isPhotoServiceAvailable] Checking API availability:', !!apiUrl);
  return !!apiUrl;
};
